import { useCallback, useRef } from 'react';
import type { UseCheckboxProps } from '.';

import type { PropGetter } from '@withjoy/joykit/utils';
import { callAllHandlers } from '@shared/utils/functions';
import { useControllableState } from '@shared/utils/hooks/useControllableState';
import { dataAttr, stopEvent } from '@shared/utils/dom';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useBoolean } from '@shared/utils/hooks/useBoolean';
import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';
import { mergeRefs } from '@shared/utils/hooks/setRef';

export const useCheckbox = (props: UseCheckboxProps) => {
  const {
    id,
    isDisabled,
    isIndeterminate,
    name,
    value,
    isChecked: isCheckedProp,
    isReadOnly,
    isRequired,
    defaultChecked,
    onBlur: onBlurProp,
    onChange: onChangeProp,
    onFocus: onFocusProp,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    ...htmlProps
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const [isCheckedState, setIsCheckedState, isControlled] = useControllableState({ value: isCheckedProp, defaultValue: defaultChecked });

  const [isFocused, setIsFocused] = useBoolean();
  // Focus *visible*, not focus. The control is an `aria-hidden` span next to a visually hidden
  // input, so it can never match `:focus-visible` itself -- the input does, and the state has to be
  // handed across as a data attribute. Tracking plain focus is what made the old ring fire on mouse
  // click too. See doc/field-focus-consistency/PLAN.md.
  const [isFocusVisible, setIsFocusVisible] = useBoolean();

  const onBlur = useEventCallback(onBlurProp);
  const onChange = useEventCallback(onChangeProp);
  const onFocus = useEventCallback(onFocusProp);

  const handleFocusVisible = useCallback(
    (event: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>) => {
      // `:focus-visible` on the input is the browser's own answer to "should this show a ring",
      // which is exactly the question the control needs answered. `currentTarget` rather than
      // `target`: it is the input on both the focus and the keydown event, and typed as such.
      let matches = false;
      try {
        matches = typeof event.currentTarget.matches === 'function' && event.currentTarget.matches(':focus-visible');
      } catch {
        // Environments that don't know the selector (older jsdom) throw rather than returning false.
        // A throw here would take down every checkbox's focus handler, so treat it as "not visible".
        matches = false;
      }
      if (matches) {
        setIsFocusVisible.on();
      } else {
        setIsFocusVisible.off();
      }
    },
    [setIsFocusVisible],
  );

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isReadOnly || isDisabled) {
        e.preventDefault();
        return;
      }
      if (!isControlled) {
        setIsCheckedState(isIndeterminate ? true : e.target.checked);
      }

      onChange?.(e);
    },
    [isControlled, isDisabled, isIndeterminate, isReadOnly, onChange, setIsCheckedState],
  );

  useIsomorphicLayoutEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = Boolean(isIndeterminate);
    }
  }, [isIndeterminate]);

  useIsomorphicLayoutEffect(() => {
    if (inputRef.current) {
      if (inputRef.current.checked !== isCheckedState) {
        setIsCheckedState(inputRef.current.checked);
      }
    }
  }, [isCheckedState, setIsCheckedState]);

  const getRootProps: PropGetter = useCallback(
    (props = {}) => {
      return {
        ...props,
        ...htmlProps,
        'data-disabled': dataAttr(isDisabled),
        'data-checked': dataAttr(isCheckedState),
      };
    },
    [htmlProps, isCheckedState, isDisabled],
  );

  const getInputProps: PropGetter<HTMLInputElement> = useCallback(
    (props = {}, forwardedRef) => {
      return {
        ...props,
        ref: mergeRefs(inputRef, forwardedRef),
        type: 'checkbox',
        id,
        name,
        value: value || '',
        checked: isCheckedState || false,
        onChange: callAllHandlers(props.onChange, handleOnChange),
        onFocus: callAllHandlers(props.onFocus, onFocus, setIsFocused.on, handleFocusVisible),
        // Focus is not the only moment `:focus-visible` can start matching. Click the control and
        // then press Space without blurring it: the browser flips the input to focus-visible on the
        // key, but nothing re-asked, so the proxy control stayed ringless for the rest of the focus.
        // Re-sampling on keydown costs one `matches()` per key and closes that window.
        onKeyDown: callAllHandlers(props.onKeyDown, handleFocusVisible),
        onBlur: callAllHandlers(props.onBlur, onBlur, setIsFocused.off, setIsFocusVisible.off),
        readOnly: isReadOnly,
        required: isRequired,
        disabled: isDisabled,
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        'aria-describedby': ariaDescribedBy,
      };
    },
    [
      ariaLabel,
      ariaLabelledBy,
      ariaDescribedBy,
      handleFocusVisible,
      handleOnChange,
      id,
      isCheckedState,
      isDisabled,
      isReadOnly,
      isRequired,
      name,
      onBlur,
      onFocus,
      setIsFocused.on,
      setIsFocused.off,
      setIsFocusVisible.off,
      value,
    ],
  );

  const getControlProps: PropGetter = useCallback(
    (props = {}) => {
      const onPressDown = (event: React.MouseEvent) => {
        // On mousedown, the input blurs and returns focus to the `body`,
        // we need to prevent this. Native checkboxes keeps focus on `input`
        event.preventDefault();
      };

      return {
        ...props,
        'aria-hidden': true,
        'data-checked': dataAttr(isCheckedState),
        'data-disabled': dataAttr(isDisabled),
        'data-indeterminate': dataAttr(isIndeterminate),
        'data-focus': dataAttr(isFocused),
        'data-focus-visible': dataAttr(isFocusVisible),
        onMouseDown: callAllHandlers(props.onMouseDown, onPressDown),
      };
    },
    [isCheckedState, isDisabled, isFocused, isFocusVisible, isIndeterminate],
  );

  const getLabelProps: PropGetter = useCallback(
    (props = {}) => {
      return {
        ...props,
        'data-disabled': dataAttr(isDisabled),

        /**
         * Prevent `onBlur` being fired when the checkbox label is touched
         */
        onMouseDown: callAllHandlers(props.onMouseDown, stopEvent),
        onTouchStart: callAllHandlers(props.onTouchStart, stopEvent),
      };
    },
    [isDisabled],
  );

  return {
    state: {
      isChecked: isCheckedState,
      isIndeterminate,
    },
    getRootProps,
    getControlProps,
    getInputProps,
    getLabelProps,
  };
};
